import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Small, opt-in hook that makes the URL query string the source of truth for a
 * SINGLE page's filter selection — and ONLY that page's keys.
 *
 * It solves two problems the per-page `syncUrl` writers had:
 *   1. Leaking: the old writers merged `new URLSearchParams(prev)` and only
 *      touched their own keys, so params owned by OTHER dashboards (e.g. a
 *      stale `brand=AMARYL` on the Sales page) rode along forever. This hook
 *      writes from a FRESH `URLSearchParams`, so foreign keys are dropped.
 *   2. Invalid hydration: a bookmarked/leaked value for a key is applied on
 *      load ONLY if it exists in that key's loaded option list; otherwise the
 *      control stays on its placeholder (null) instead of showing a dead value.
 *
 * This hook is intentionally generic and page-agnostic. A page opts in by
 * passing its own `keys` (the only params it owns). Nothing here changes other
 * pages — each page decides whether to adopt it.
 *
 * Wire formats are left untouched: values are read and written verbatim. If a
 * page uses a different UI↔wire encoding (e.g. Division Performance's
 * YYYY-MM ↔ YYYYMM month), it maps around this hook; this hook never rewrites
 * a value's shape.
 *
 * @param {readonly string[]} keys
 *   The query-param keys this page owns, e.g. `['fy','month','division',...]`.
 * @returns {{
 *   readParam: (key: string) => (string | null),
 *   readValidParam: (key: string, isValid: (value: string) => boolean) => (string | null),
 *   writeOwnParams: (values: Record<string, string | null | undefined>) => void,
 * }}
 */
export function useUrlFilters(keys) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Freeze the owned-key set once; identity only changes if the page passes a
  // genuinely different list (it won't — pages pass a module-level constant).
  const ownKeys = useMemo(() => Array.from(keys), [keys]);

  /** Raw read of an owned key from the URL (null when absent/empty). */
  const readParam = useCallback(
    (key) => {
      const raw = searchParams.get(key);
      return raw == null || raw === '' ? null : raw;
    },
    [searchParams],
  );

  /**
   * Read an owned key, but return it only if `isValid(value)` passes (typically
   * "exists in the loaded options"). Returns null otherwise so the control
   * falls back to its placeholder/default instead of a dead value.
   */
  const readValidParam = useCallback(
    (key, isValid) => {
      const value = readParam(key);
      if (value == null) return null;
      return isValid(value) ? value : null;
    },
    [readParam],
  );

  /**
   * Write ONLY this page's owned keys. Builds a fresh `URLSearchParams` so any
   * param owned by another page is deleted. Non-empty values among `ownKeys`
   * are set; everything else (including unlisted keys) is removed.
   * Uses `replace: true` to keep browser history clean.
   */
  const writeOwnParams = useCallback(
    (values) => {
      setSearchParams(
        () => {
          const params = new URLSearchParams();
          for (const key of ownKeys) {
            const value = values[key];
            if (value != null && value !== '') params.set(key, String(value));
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams, ownKeys],
  );

  return { readParam, readValidParam, writeOwnParams };
}
